import { createFileRoute } from "@tanstack/react-router";
import { MarketingPage, Section, Callout } from "@/components/marketing-page";
import { marketingHead, type FAQ } from "@/lib/seo";

const PATH = "/security";
const TITLE = "Security & privacy | monroya.ai";
const DESCRIPTION =
  "How Monroya handles your data: auth, hosting, what we store, subprocessors, retention, deletion, and compliance posture.";

const FAQS: FAQ[] = [
  {
    q: "Do you train AI models on my data?",
    a: "No. We query third-party AI providers (OpenAI, Anthropic, Google, Perplexity) to measure how they respond to public, category-level prompts. We do not send your private data, account contents, or scan history to those providers, and we do not train any models on customer data.",
  },
  {
    q: "Where is my data hosted?",
    a: "On US-region cloud infrastructure managed by our hosting provider, with database backups encrypted at rest. All access to production data is gated behind SSO and role-based access controls and is logged.",
  },
  {
    q: "Are you SOC 2 certified?",
    a: "Not yet. We have implemented controls aligned with SOC 2 Type II (access control, change management, encryption, logging) but have not completed an external audit. We will publish the report here when the audit is complete. If your procurement requires an in-flight audit letter today, email us.",
  },
  {
    q: "Can I delete my account and data?",
    a: "Yes. Account deletion is self-serve from the settings page. We hard-delete account records, scans, drafts, source ledgers, and crawl metadata within 30 days of the deletion request. Backups are purged on the next backup rotation cycle (≤30 days).",
  },
  {
    q: "Who do I contact about a security issue?",
    a: "Email support@monroya.ai with details. We acknowledge within one business day and aim to resolve critical issues within 7 days. Responsible disclosure is welcome.",
  },
];

export const Route = createFileRoute("/security")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Security", path: PATH },
      ],
      faqs: FAQS,
    }),
  component: SecurityPage,
});

function SecurityPage() {
  return (
    <MarketingPage
      stamp="Security & privacy"
      title="Monroya security and data privacy"
      dek="The honest version. What we collect, where it lives, who can touch it, what we're certified on today, and what we're not."
      updated="October 2026"
      faqs={FAQS}
      related={[
        { label: "Methodology", to: "/methodology" },
        { label: "Privacy policy", to: "/privacy" },
        { label: "Terms of service", to: "/terms" },
      ]}
    >
      <Callout label="Maintained by Monroya">
        This page is maintained by the Monroya team to answer common
        security and privacy questions. It describes app-visible controls
        and current practices, not an independent audit or certification.
        For procurement paperwork, email{" "}
        <a
          href="mailto:support@monroya.ai"
          className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
        >
          support@monroya.ai
        </a>
        .
      </Callout>

      <Section heading="Authentication & access" id="auth">
        <p>
          Customer accounts use email + password or Google SSO via our
          managed auth provider. Sessions are JWT-backed and expire on a
          rolling window. Production database access is limited to a small
          set of engineers, gated behind SSO with hardware key MFA, and
          fully audit-logged.
        </p>
      </Section>

      <Section heading="Hosting" id="hosting">
        <p>
          The application runs on a serverless edge runtime in front of a
          managed Postgres database. Data is stored in US regions. All
          traffic uses TLS 1.2 or higher. Backups are encrypted at rest
          and tested on a rotating schedule.
        </p>
      </Section>

      <Section heading="What we collect" id="collect">
        <p>
          The minimum required to run scans and produce opportunities:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Account profile, email, name, organization, role.</li>
          <li>Site crawl metadata, public-page content, schema, links, used to build your prompt set and source ledger.</li>
          <li>Prompts you confirm and scans you run, including the provider responses and extracted citations.</li>
          <li>Product usage, which features you open, used for billing and improving the product. We don't sell or share this.</li>
        </ul>
        <p>
          We don't ask for or store source-of-truth analytics, CRM, or
          revenue data. Monroya doesn't need it to do its job.
        </p>
      </Section>

      <Section heading="What we share with AI providers" id="ai-providers">
        <p>
          During scans we query public AI providers (OpenAI, Anthropic,
          Google, Perplexity) with the prompts you've confirmed. Those
          queries look identical to any other API call, the providers do
          not see your account identity, your scan history, or any other
          customer's data. We don't send private content to them and we
          don't train any models on customer data.
        </p>
      </Section>

      <Section heading="Subprocessors" id="subprocessors">
        <p>
          The third-party services that process customer data on our behalf:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          
          <li><strong className="text-ink">Email delivery</strong> - Resend, for transactional emails (account, payment, alerts).</li>
          <li><strong className="text-ink">Payment processing</strong> - Stripe. Card data never touches our infrastructure.</li>
          <li><strong className="text-ink">AI providers (scan-time only)</strong> - OpenAI, Anthropic, Google, Perplexity. See above.</li>
          <li><strong className="text-ink">Error monitoring</strong> - used for crash/error telemetry only; scrubbed of PII.</li>
        </ul>
        <p>
          We notify customers at least 30 days before adding or replacing a
          subprocessor that processes customer data.
        </p>
      </Section>

      <Section heading="Retention & deletion" id="retention">
        <p>
          Active accounts retain scans and drafts indefinitely so you can
          see historical movement. On account deletion we hard-delete
          records within 30 days; encrypted backup snapshots are purged
          on the next rotation (≤30 days). Inactive free accounts are purged after 90 days without a sign-in.
        </p>
      </Section>

      <Section heading="Compliance posture" id="compliance">
        <p>
          Honest snapshot, no marketing-spun badges:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li><strong className="text-ink">GDPR</strong> - DPA available on request for paying customers.</li>
          <li><strong className="text-ink">SOC 2 Type II</strong> - controls implemented, audit not yet completed. If your procurement needs status today, email us.</li>
          <li><strong className="text-ink">HIPAA / FedRAMP / ISO 27001</strong> - not in scope.</li>
        </ul>
        <p>
          We will publish certifications on this page only after the
          underlying audit is complete. We don't list "compliant" badges
          we haven't earned.
        </p>
      </Section>

      <Section heading="Reporting a security issue" id="report">
        <p>
          Email{" "}
          <a
            href="mailto:support@monroya.ai"
            className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
          >
            support@monroya.ai
          </a>
          . We acknowledge within one business day. Responsible
          disclosure is welcome.
        </p>
      </Section>
    </MarketingPage>
  );
}
